import { useState } from "react";
import { SignUpForm } from "./SignUpForm";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Capacitor } from "@capacitor/core";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { SUBSCRIPTION_PLANS, type SubscriptionType } from "@/types/subscription";
import { purchaseRevenueCatProduct } from "@/utils/revenuecatPurchase";
import { trackSignUp } from "@/services/appsflyer";

export const SignUpFormContainer = () => {
  const { toast } = useToast();
  const [isProcessing, setIsProcessing] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [showResendDialog, setShowResendDialog] = useState(false);
  const [resendEmail, setResendEmail] = useState<string | null>(null);
  const [isResending, setIsResending] = useState(false);
  const [selectedSubscription, setSelectedSubscription] = useState<SubscriptionType>("select");
  const [hasActivePurchase, setHasActivePurchase] = useState(false);
  const isNative = Capacitor.isNativePlatform();
  const isIOS = isNative && Capacitor.getPlatform() === "ios";

  const getEmailRedirectTo = () => {
    if (isNative) {
      // On iOS native, redirect to THIS project's EmailVerified page which has the mindnote:// scheme redirect logic
      return "https://ios.mindnote.online/email-verified";
    }
    // Web fallback
    return "https://workspace.mindnote.online/email-verified";
  };
  const handleResendVerification = async (email: string) => {
    try {
      setIsResending(true);
      const redirectUrl = getEmailRedirectTo();
      const { error } = await supabase.auth.resend({
        type: "signup",
        email,
        options: { emailRedirectTo: redirectUrl },
      });
      if (error) throw error;
      toast({
        title: "Verification email resent",
        description: "Please check your inbox for a new verification link.",
      });
      setShowResendDialog(false);
      setShowSuccessMessage(true);
    } catch (err: any) {
      console.error("SignUpFormContainer: Resend error:", err);
      toast({
        title: "Could not resend email",
        description: err.message || "Please try again in a few minutes.",
        variant: "destructive",
      });
    } finally {
      setIsResending(false);
    }
  };

  const handleSubscriptionChange = async (subscriptionType: SubscriptionType) => {
    setSelectedSubscription(subscriptionType);

    // If user selects monthly or yearly on iOS, trigger Apple billing
    if (isIOS && (subscriptionType === "monthly" || subscriptionType === "yearly")) {
      await handleIOSSubscription(subscriptionType);
    }
  };

  const handleIOSSubscription = async (subscriptionType: SubscriptionType) => {
    if (isProcessing) return;

    try {
      setIsProcessing(true);

      // Find the plan details
      const plan = SUBSCRIPTION_PLANS.find(
        (p) =>
          (subscriptionType === "monthly" && p.interval === "month") ||
          (subscriptionType === "yearly" && p.interval === "year")
      );

      if (!plan) {
        throw new Error("Subscription plan not found");
      }

      // Purchase (uses Offering packages when configured, otherwise falls back to direct product purchase)
      const { customerInfo } = await purchaseRevenueCatProduct({ productId: plan.id });

      console.log("Purchase successful, customer info:", customerInfo);

      // Check if the purchase was successful
      const hasActiveSubscription = Object.keys(customerInfo.entitlements.active).length > 0;

      if (hasActiveSubscription) {
        setHasActivePurchase(true);
        toast({
          title: "Subscription activated!",
          description: `You're now subscribed to ${plan.name}. Complete your signup to continue.`,
        });
      } else {
        throw new Error("Purchase completed but no active entitlement found");
      }
    } catch (error: any) {
      console.error("Subscription error:", error);

      if (error.code === "PURCHASE_CANCELLED" || error.userCancelled) {
        toast({
          title: "Purchase cancelled",
          description: "You cancelled the subscription purchase.",
        });
        // Reset subscription selection
        setSelectedSubscription("select");
      } else {
        toast({
          title: "Subscription failed",
          description: error.message || "Please try again later.",
          variant: "destructive",
        });
        // Reset subscription selection
        setSelectedSubscription("select");
      }
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSignUp = async (formData: {
    email: string;
    password: string;
    username: string;
    subscriptionType: SubscriptionType;
  }) => {
    if (isProcessing) return;
    setIsProcessing(true);
    setShowSuccessMessage(false);
    console.log("SignUpFormContainer: Starting signup process for:", formData.email);

    try {
      if (!formData.username.startsWith("@")) {
        toast({
          title: "Invalid username",
          description: "Username must start with @",
          variant: "destructive",
        });
        return;
      }

      // Get referral code from URL if it exists
      const urlParams = new URLSearchParams(window.location.search);
      const referralCode = urlParams.get("ref");

      // Store subscription selection for after email verification
      const pendingData: any = {
        redirectAfterPayment: "/auth",
        selectedSubscription: formData.subscriptionType,
      };

      if (referralCode) {
        pendingData.referralCode = referralCode;
      }

      localStorage.setItem("pendingSignup", JSON.stringify(pendingData));

      const redirectUrl = getEmailRedirectTo();
      console.log("SignUpFormContainer: Using redirect URL:", redirectUrl);

      // Sign up with Supabase
      console.log("SignUpFormContainer: Creating Supabase account...");
      const { data, error: signUpError } = await supabase.auth.signUp({
        email: formData.email,
        password: formData.password,
        options: {
          data: {
            username: formData.username,
            subscription_type: hasActivePurchase ? formData.subscriptionType : "none",
          },
          emailRedirectTo: redirectUrl,
        },
      });

      if (signUpError) {
        // If the user already exists (possibly unverified), offer to resend verification
        const message = (signUpError.message || "").toLowerCase();
        if (message.includes("already") && message.includes("registered")) {
          setResendEmail(formData.email);
          setShowResendDialog(true);
          return;
        }
        console.error("SignUpFormContainer: Signup error:", signUpError);
        throw signUpError;
      }

      if (!data?.user) {
        throw new Error("No user data returned from signup");
      }

      console.log("SignUpFormContainer: Signup successful", {
        userId: data.user?.id,
        emailConfirmed: data.user?.email_confirmed_at,
        needsEmailConfirmation: !data.user?.email_confirmed_at,
      });

      // Track sign-up event in AppsFlyer
      trackSignUp('email');

      // Show success message
      setShowSuccessMessage(true);
      toast({
        title: "Account created",
        description: hasActivePurchase
          ? "Please check your email to verify your account. Your subscription is active!"
          : "Please check your email to verify your account. After verification, you can complete your subscription purchase.",
      });
    } catch (error: any) {
      console.error("SignUpFormContainer: Error in signup process:", error);
      toast({
        title: "Error signing up",
        description: error.message || "An unexpected error occurred",
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <>
      <SignUpForm
        onSubmit={handleSignUp}
        isProcessing={isProcessing}
        showSuccessMessage={showSuccessMessage}
        onSubscriptionChange={handleSubscriptionChange}
        subscriptionType={selectedSubscription}
      />

      <Dialog open={showResendDialog} onOpenChange={setShowResendDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Finish setting up your account</DialogTitle>
            <DialogDescription>
              It looks like you started signing up before but didn't verify your email. Would you like us to resend the
              verification email to {resendEmail}?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowResendDialog(false)} disabled={isResending}>
              Cancel
            </Button>
            <Button onClick={() => resendEmail && handleResendVerification(resendEmail)} disabled={isResending}>
              {isResending ? "Resending…" : "Resend verification email"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
